/**
 * Date-grouping helpers shared by every feed that renders day separators with
 * a jump-to-date affordance (any scrolling timeline). Kept free of any
 * domain types so the date-jump module stays generic.
 */

/**
 * Format a local Date as a `YYYY-MM-DD` key.
 *
 * We deliberately read the local Y/M/D rather than `toISOString()`, which
 * would shift to UTC and silently disagree with the server (which keys by the
 * viewer's local zone via `$timezone`) and with the popover's local
 * today/yesterday math for non-UTC viewers.
 */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Human-readable label for a day group: "Today", "Yesterday", or
 * "Monday, June 3rd" (with a `, YYYY` suffix for prior years).
 */
export function getDateLabel(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groupDate = new Date(date);
  groupDate.setHours(0, 0, 0, 0);

  if (groupDate.getTime() === today.getTime()) return "Today";
  if (groupDate.getTime() === yesterday.getTime()) return "Yesterday";

  const day = groupDate.getDate();
  const mod10 = day % 10;
  const mod100 = day % 100;

  let suffix = "th";
  if (mod10 === 1 && mod100 !== 11) suffix = "st";
  else if (mod10 === 2 && mod100 !== 12) suffix = "nd";
  else if (mod10 === 3 && mod100 !== 13) suffix = "rd";

  const weekdayAndMonth = groupDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
  });

  const year =
    groupDate.getFullYear() !== today.getFullYear()
      ? `, ${groupDate.getFullYear()}`
      : "";

  return `${weekdayAndMonth} ${day}${suffix}${year}`;
}
